// Audio input device enumeration + capability detection.
//
// `enumerateDevices` only returns full labels AFTER the user has granted
// microphone permission at least once. So we run a one-shot `getUserMedia`
// to unlock labels, then immediately stop the temporary stream.

import { mapMicError } from "./micCheck";

export async function listInputDevices({ unlockLabels = true } = {}) {
  if (!navigator?.mediaDevices?.enumerateDevices) {
    return { devices: [], error: "unsupported-browser" };
  }
  // Unlock device labels (only needs to succeed once per origin)
  if (unlockLabels) {
    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
      tmp.getTracks().forEach((t) => t.stop());
    } catch (e) {
      const mapped = mapMicError(e);
      return { devices: [], error: mapped.code, micError: mapped };
    }
  }
  const all = await navigator.mediaDevices.enumerateDevices();
  const inputs = all
    .filter((d) => d.kind === "audioinput")
    .map((d) => ({
      deviceId: d.deviceId,
      label: d.label || `Input · ${d.deviceId.slice(0, 6)}`,
      groupId: d.groupId,
    }));
  return { devices: inputs, error: null };
}

/** Try to detect the max channel count for a device. Web Audio doesn't
 *  expose this directly — we have to open a temporary stream with a wide
 *  channelCount constraint and see what we actually got. */
export async function probeChannelCount(deviceId, attempt = 64) {
  if (!navigator?.mediaDevices?.getUserMedia) return 0;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        channelCount: { ideal: attempt },
      },
    });
    const track = stream.getAudioTracks()[0];
    const settings = track.getSettings();
    const reportedChannels = Number(settings.channelCount) || 0;
    stream.getTracks().forEach((t) => t.stop());
    return reportedChannels;
  } catch (e) {
    return 0;
  }
}
